function createMobileSearchButton() {
  const fixedSearchOffsetThreshold = 250;
  const fixedSearchTransitionDuration = 2000;
  const header = document.querySelector('[data-gs-header]');
  const headerActions = header?.querySelector('.gs-header-right');
  const cartLink = headerActions?.querySelector('a[aria-label="Cart"]');
  const mobileSearch = header?.querySelector('.gs-mobile-search');
  const mobileSearchInput = mobileSearch?.querySelector('[data-gs-live-input]');
  const headerSection = header?.closest('.shopify-section');

  if (
    !header ||
    !headerActions ||
    !cartLink ||
    !mobileSearch ||
    !mobileSearchInput ||
    !headerSection
  ) return;

  const originalSearchParent = mobileSearch.parentElement;
  const searchPositionMarker = document.createComment('mobile-search-position');
  originalSearchParent.insertBefore(searchPositionMarker, mobileSearch);

  const searchButton = document.createElement('button');
  searchButton.type = 'button';
  searchButton.className = 'gs-icon-btn gs-mobile-scroll-search';
  searchButton.setAttribute('aria-label', 'Open product search');
  searchButton.setAttribute('aria-expanded', 'false');
  searchButton.innerHTML = `
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="6.5" stroke="currentColor" stroke-width="1.7"></circle>
      <path d="m16 16 4 4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"></path>
    </svg>
  `;

  headerActions.insertBefore(searchButton, cartLink);
  searchButton.tabIndex = -1;

  let scrollFrame = null;
  let touchStartY = null;
  let resultTouchLink = null;
  let resultTouchStartX = null;
  let resultTouchStartY = null;
  let searchCloseTimer = null;
  let searchSlot = null;
  let isSearchCollapsed = false;
  let isSearchInputFocused = false;
  let lastScrollY = Math.max(window.scrollY, 0);
  let scrollDirection = 'down';
  let hasEnteredFixedRange = false;

  const placeSearchInMobileFlow = () => {
    if (!searchSlot) {
      searchSlot = document.createElement('div');
      searchSlot.className = 'gs-mobile-search-slot';
      headerSection.insertAdjacentElement('afterend', searchSlot);
    }

    if (mobileSearch.parentElement !== searchSlot) searchSlot.append(mobileSearch);
  };

  const restoreDesktopSearchPlacement = () => {
    window.clearTimeout(searchCloseTimer);
    searchCloseTimer = null;
    hasEnteredFixedRange = false;
    mobileSearch.classList.remove(
      'gs-mobile-search--fixed',
      'gs-mobile-search--fixed-open',
      'gs-mobile-search--transition-ready'
    );
    originalSearchParent.insertBefore(mobileSearch, searchPositionMarker.nextSibling);
    searchSlot?.remove();
    searchSlot = null;
  };

  const setSearchCollapsed = shouldCollapse => {
    isSearchCollapsed = shouldCollapse;
    header.classList.toggle('gs-header--mobile-search-collapsed', shouldCollapse);
    searchButton.style.pointerEvents = shouldCollapse ? 'auto' : 'none';
    searchButton.tabIndex = shouldCollapse ? 0 : -1;
    mobileSearch.style.pointerEvents = shouldCollapse ? 'none' : 'auto';
  };

  const resetCollapseProgress = () => {
    header.classList.remove('gs-header--mobile-search-collapsed');
    searchButton.style.pointerEvents = 'none';
    searchButton.tabIndex = -1;
    mobileSearch.style.pointerEvents = 'auto';
    isSearchCollapsed = false;
  };

  const closeSearch = ({ restoreFocus = false } = {}) => {
    header.classList.remove('gs-header--mobile-search-open');
    searchButton.setAttribute('aria-expanded', 'false');
    mobileSearch.classList.remove('gs-mobile-search--fixed-open');

    window.clearTimeout(searchCloseTimer);
    searchCloseTimer = window.setTimeout(() => {
      if (header.classList.contains('gs-header--mobile-search-open')) return;

      mobileSearch.classList.remove(
        'gs-mobile-search--fixed',
        'gs-mobile-search--transition-ready'
      );
      searchCloseTimer = null;
    }, fixedSearchTransitionDuration);

    if (restoreFocus) searchButton.focus({ preventScroll: true });
  };

  const prepareFixedSearch = () => {
    if (mobileSearch.classList.contains('gs-mobile-search--fixed')) return;

    mobileSearch.classList.remove('gs-mobile-search--transition-ready');
    mobileSearch.classList.add('gs-mobile-search--fixed');
    mobileSearch.getBoundingClientRect();
    mobileSearch.classList.add('gs-mobile-search--transition-ready');
  };

  const setFixedSearchVisible = shouldShow => {
    window.clearTimeout(searchCloseTimer);
    searchCloseTimer = null;

    prepareFixedSearch();
    mobileSearch.classList.toggle('gs-mobile-search--fixed-open', shouldShow);
    setSearchCollapsed(!shouldShow);
  };

  const restoreSearchToFlow = () => {
    window.clearTimeout(searchCloseTimer);
    searchCloseTimer = null;
    mobileSearch.classList.remove(
      'gs-mobile-search--fixed',
      'gs-mobile-search--fixed-open',
      'gs-mobile-search--transition-ready'
    );
    setSearchCollapsed(false);
  };

  const keepSearchCollapsedInFlow = () => {
    window.clearTimeout(searchCloseTimer);
    searchCloseTimer = null;
    mobileSearch.classList.remove(
      'gs-mobile-search--fixed',
      'gs-mobile-search--fixed-open',
      'gs-mobile-search--transition-ready'
    );
    setSearchCollapsed(true);
  };

  const startOpenedSearchCollapse = () => {
    if (!header.classList.contains('gs-header--mobile-search-open')) return;

    mobileSearchInput.blur();
    setSearchCollapsed(true);
    closeSearch();
  };

  const getSearchOffsetUnderHeader = () => {
    if (!searchSlot) return 0;

    const headerBottom = headerSection.getBoundingClientRect().bottom;
    const searchBottom = searchSlot.getBoundingClientRect().bottom;

    return headerBottom - searchBottom;
  };

  const updateScrolledState = () => {
    const isMobile = window.matchMedia('(max-width: 1360px)').matches;
    const currentScrollY = Math.max(window.scrollY, 0);
    const scrollDelta = currentScrollY - lastScrollY;

    if (Math.abs(scrollDelta) >= 4) {
      scrollDirection = scrollDelta > 0 ? 'down' : 'up';
      lastScrollY = currentScrollY;
    }

    if (!isMobile) {
      document.documentElement.classList.remove('gs-mobile-search-scroll-active');
      resetCollapseProgress();
      closeSearch();
      restoreDesktopSearchPlacement();
    } else {
      document.documentElement.classList.add('gs-mobile-search-scroll-active');
      placeSearchInMobileFlow();
    }

    if (!isMobile) {
      return;
    }

    const searchOffsetUnderHeader = getSearchOffsetUnderHeader();
    const searchHasPassed = searchOffsetUnderHeader >= -1;

    if (!searchHasPassed) {
      hasEnteredFixedRange = false;
      restoreSearchToFlow();
      return;
    }

    if (
      !hasEnteredFixedRange &&
      searchOffsetUnderHeader >= fixedSearchOffsetThreshold
    ) {
      hasEnteredFixedRange = true;
      setFixedSearchVisible(false);
      return;
    }

    if (!hasEnteredFixedRange) {
      keepSearchCollapsedInFlow();
      return;
    }

    const searchIsActive =
      header.classList.contains('gs-header--mobile-search-open') ||
      isSearchInputFocused;
    const shouldShowFixedSearch = searchIsActive || scrollDirection === 'up';

    setFixedSearchVisible(shouldShowFixedSearch);
  };

  const scheduleScrolledStateUpdate = () => {
    if (scrollFrame !== null) return;

    scrollFrame = window.requestAnimationFrame(() => {
      scrollFrame = null;
      updateScrolledState();
    });
  };

  searchButton.addEventListener('click', () => {
    placeSearchInMobileFlow();
    hasEnteredFixedRange = true;
    window.clearTimeout(searchCloseTimer);
    searchCloseTimer = null;
    prepareFixedSearch();
    mobileSearch.classList.add('gs-mobile-search--fixed-open');
    header.classList.add('gs-header--mobile-search-open');
    searchButton.setAttribute('aria-expanded', 'true');
    setSearchCollapsed(false);
    mobileSearchInput.focus({ preventScroll: true });
  });

  mobileSearchInput.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;

    closeSearch({ restoreFocus: true });
    scheduleScrolledStateUpdate();
  });

  mobileSearchInput.addEventListener('focus', () => {
    isSearchInputFocused = true;
    setSearchCollapsed(false);
  });

  mobileSearchInput.addEventListener('blur', () => {
    isSearchInputFocused = false;
  });

  const resetResultTouch = () => {
    resultTouchLink = null;
    resultTouchStartX = null;
    resultTouchStartY = null;
  };

  mobileSearch.addEventListener(
    'touchstart',
    event => {
      const link = event.target.closest?.('.gs-live-result-link, .gs-live-result-all');
      const touch = event.touches[0];

      if (!link || !touch || !mobileSearch.contains(link)) {
        resetResultTouch();
        return;
      }

      resultTouchLink = link;
      resultTouchStartX = touch.clientX;
      resultTouchStartY = touch.clientY;
    },
    { passive: true }
  );

  mobileSearch.addEventListener(
    'touchmove',
    event => {
      if (!resultTouchLink) return;

      const touch = event.touches[0];
      if (!touch) {
        resetResultTouch();
        return;
      }

      const horizontalMovement = Math.abs(touch.clientX - resultTouchStartX);
      const verticalMovement = Math.abs(touch.clientY - resultTouchStartY);

      if (horizontalMovement > 10 || verticalMovement > 10) resetResultTouch();
    },
    { passive: true }
  );

  mobileSearch.addEventListener(
    'touchend',
    event => {
      const link = resultTouchLink;
      resetResultTouch();

      if (!link) return;

      event.preventDefault();

      try {
        const destination = new URL(link.href, window.location.href);

        if (destination.origin !== window.location.origin) return;

        window.location.assign(destination.href);
      } catch (error) {
        console.error('Unable to open the selected search result:', error);
      }
    },
    { passive: false }
  );

  mobileSearch.addEventListener('touchcancel', resetResultTouch, { passive: true });

  window.addEventListener(
    'touchstart',
    event => {
      if (!header.classList.contains('gs-header--mobile-search-open')) return;
      if (mobileSearch.contains(event.target)) return;

      touchStartY = event.touches[0]?.clientY ?? null;
    },
    { passive: true, capture: true }
  );

  window.addEventListener(
    'touchmove',
    event => {
      if (touchStartY === null) return;

      const currentY = event.touches[0]?.clientY;
      if (currentY === undefined || Math.abs(currentY - touchStartY) < 8) return;

      touchStartY = null;
      startOpenedSearchCollapse();
    },
    { passive: true, capture: true }
  );

  const resetTouch = () => {
    touchStartY = null;
  };

  window.addEventListener('touchend', resetTouch, { passive: true, capture: true });
  window.addEventListener('touchcancel', resetTouch, { passive: true, capture: true });

  window.addEventListener(
    'wheel',
    event => {
      if (!header.classList.contains('gs-header--mobile-search-open')) return;
      if (Math.abs(event.deltaY) < 4) return;

      startOpenedSearchCollapse();
    },
    { passive: true }
  );

  window.addEventListener('scroll', scheduleScrolledStateUpdate, { passive: true });
  window.addEventListener('resize', () => {
    if (isSearchInputFocused || document.activeElement === mobileSearchInput) return;

    scheduleScrolledStateUpdate();
  });
  updateScrolledState();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', createMobileSearchButton);
} else {
  createMobileSearchButton();
}
